import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useSession } from './SessionContext';
import { setSessionManager, setOnTokenRefreshed } from '../api/axiosConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const {
    session,
    loading: sessionLoading,
    isAuthenticated,
    login: sessionLogin,
    logout: sessionLogout,
    refreshToken: sessionRefreshToken,
    sessionManager,
    checkAuth,
    getSession,
    syncFromStorage,
  } = useSession();

  const [user, setUser] = useState(null);

  // Wire the session manager into axios
  useEffect(() => {
    if (sessionManager) {
      setSessionManager(sessionManager);
    }
  }, [sessionManager]);

  // Wire the axios → React sync callback
  useEffect(() => {
    if (syncFromStorage) {
      setOnTokenRefreshed(() => {
        syncFromStorage();
      });
    }
  }, [syncFromStorage]);

  // Update user when session changes
  useEffect(() => {
    if (isAuthenticated && session) {
      setUser({ name: session.userName || 'User' });
    } else {
      setUser(null);
    }
  }, [session, isAuthenticated]);

  // Login wrapper
  const login = useCallback(
    async (username, password) => {
      const result = await sessionLogin(username, password);
      if (result.success) {
        setUser({ name: result.user.name });
      }
      return result;
    },
    [sessionLogin]
  );

  // Logout wrapper
  const logout = useCallback(async () => {
    await sessionLogout();
    setUser(null);
  }, [sessionLogout]);

  // Refresh wrapper
  const refreshToken = useCallback(async () => {
    return await sessionRefreshToken();
  }, [sessionRefreshToken]);

  const loading = sessionLoading;

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    refreshToken,
    checkAuth,
    getSession,
    sessionManager,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};